import { RecurrenceType, RepeatInterval } from '../common/shared-dtos-scheduler-common';

// DB module format
export interface AntimalwareModule {
  antiTampering: AntimalwareAntiTamperingSection;
  onAccess: AntimalwareOnAccessSection;
  dynamicThreatDefense: AntimalwareDynamicThreatDefenseSection;
  antiExploit: AntimalwareAntiExploitSection;
  securityServers: AntimalwareSecurityServersSection;
  onExecute: AntimalwareOnExecuteSection;
  onDemand: AntimalwareOnDemandSection;
  settings: AntimalwareSettingsSection;
}

export interface AntimalwareSettingsSection {
  deleteFilesOlderThan: AntimalwareSettingsDeleteFilesOlderThanType;
  submitQuarantined: {
    enable: boolean;
    hours: AntimlawareSettingsSubmitQuarantinedType;
  };
  rescanQuarantine: boolean;
  copyFilesToQuarantineBeforeDisinfect: boolean;
  allowUserQuarantineActions: boolean;
  useBuiltInExclusions: boolean;
  activateExclusions: {
    enable: boolean;
    exclusionsItems: AntimalwareSettingsExclusionsItem[];
    useVendorExclusionLists: boolean;
    useExclusionLists: boolean;
    selectedVendorExclusionLists: string[];
    exclusionsLists: string[];
  };
  centralizedQuarantine?: {
    enable: boolean;
    archivePassword: string;
    location: {
      path: string;
      username: string;
      password: string;
    };
    autoSubmitToSandboxAnalyzer: boolean;
  };
}

export const ANTIMALWARE_EXCLUSION_TYPE = 1;
export interface AntimalwareExclusionsListResponse {
  _id: string;
  name: string;
  description: string;
  endpointsCount: number;
  policies: { id: string; name: string; policyEndpointsCount: number }[];
}
export interface AntimalwareVendorsListResponse {
  _id: string;
  name: string;
}

export enum AntimlawareSettingsSubmitQuarantinedType {
  ONE_HOUR = 1,
  TWO_HOURS = 2,
  FOUR_HOURS = 4,
  SIX_HOURS = 6,
  EIGHT_HOURS = 8,
  TWELVE_HOURS = 12,
  TWENTY_FOUR_HOURS = 24,
}

export enum AntimalwareSettingsDeleteFilesOlderThanType {
  ONE_DAY = 1,
  TWO_DAYS = 2,
  THREE_DAYS = 3,
  FOUR_DAYS = 4,
  FIVE_DAYS = 5,
  SIX_DAYS = 6,
  SEVEN_DAYS = 7,
  FIFTEEN_DAYS = 15,
  THIRTY_DAYS = 30,
  SIXTY_DAYS = 60,
  ONE_HUNDRED_TWENTY_DAYS = 120,
  ONE_HUNDRED_EIGHTY_DAYS = 180,
}

export interface AntimalwareSettingsExclusionsItem {
  taskType: AntimalwareSettingsExclusionsTaskType;
  path: string;
  module: AntimalwareSettingsExclusionsModuleType[];
  remarks: string;
}

export enum AntimalwareSettingsExclusionsTaskType {
  FILE = 1,
  FOLDER = 2,
  EXTENSION = 3,
  PROCESS = 4,
  FILE_HASH = 5,
  CERTIFICATE_HASH = 6,
  THREAT_NAME = 7,
  CMD_PARAMS = 8,
  IP_MASK = 9,
  CMD_PARAMS_REGEX = 10,
}

export enum AntimalwareSettingsExclusionsModuleType {
  ON_DEMAND = 1,
  ON_ACCESS = 2,
  ALL = 3,
  AVC_IDS = 4,
  PROCESS_ALL = 5,
  RANSOMWARE_REMEDIATION = 6,
  LSASS_PROTECTION = 7,
  DPI_DETECTIONS = 8,
  ENTIRE_AAE = 9,
  SENSITIVE_REGISTRY_PROTECTION = 10,
}

// antiTampering
export interface AntimalwareAntiTamperingSection {
  enabled: boolean;
  vulnerableDrivers: {
    enabled: boolean;
    action: AntimalwareAntiTamperingVulnerableDriversAction;
  };
  callbackEvasion: {
    enabled: boolean;
    actions: AntimalwareAntiTamperingCallbackEvasionActions;
  };
}

export interface AntimalwareOnAccessSection {
  onAccessScanning: AntimalwareOnAccessScanning;
}

export interface AntimalwareOnAccessScanning {
  enable: boolean;
  profile: AntimalwareOnAccessScanningProfileType;
  settings: AntimalwareOnAccessScanningSettings;
}

export interface AntimalwareOnAccessScanningSettings {
  general: AntimalwareOnAccessScanningGeneralSettings;
  advanced: AntimalwareOnAccessScanningAdvancedSettings;
}

interface AntimalwareOnAccessScanningGeneralSettings {
  fileTypes: AntimalwareOnAccessScanningGeneralFileTypes;
  archives: AntimalwareOnAccessScanningGeneralArchives;
  miscellaneous: AntimalwareOnAccessScanningGeneralMiscellaneous;
  scanAction: AntimalwareOnAccessScanningGeneralScanAction;
}
interface AntimalwareOnAccessScanningAdvancedSettings {
  onAccessUnix: boolean;
  onAccessUnixPaths: string[];
}

interface AntimalwareOnAccessScanningGeneralFileTypes {
  scanLocalFile: {
    enable: boolean;
    settings: {
      fileType: AntimalwareFileTypesStatus;
      extensions: string | string[];
    };
  };
  scanNetworkFile: {
    enable: boolean;
    settings: {
      fileType: AntimalwareFileTypesStatus;
      extensions: string | string[];
    };
  };
  limitFile: {
    enable: boolean;
    size: number;
  };
}
interface AntimalwareOnAccessScanningGeneralArchives {
  enable: boolean;
  limitArchiveSize: number;
  maximumArchiveDepth: AntimalwareArchivesDepth;
}
interface AntimalwareOnAccessScanningGeneralMiscellaneous {
  scanBootSectors: boolean;
  scanOnlyNewChangeFiles: boolean;
  scanForKeyloggers: boolean;
  scanPUA: boolean;
  deferredScanning: boolean;
  scanProcessMemory: boolean;
}
interface AntimalwareOnAccessScanningGeneralScanAction {
  infectedFiles: {
    action: AntimalwareOnAccessScanningGeneralScanActionInfectedFilesAction;
  };
}

export interface AntimalwareAntiTamperingCallbackEvasionActions {
  isolateEndpoint: boolean;
  rebootEndpoint: boolean;
  rebootTime: AntimalwareAntiTamperingRebootTimes;
}

// dynamicThreatDefense
export interface AntimalwareDynamicThreatDefenseSection {
  enable: boolean;
  enableGlobalReportingForFiles: boolean;
  enableGlobalReportingForNetwork: boolean;
  actionforLocal: AntimalwareDynamicThreatDefenseActionForLocalType;
  actionforNetwork: AntimalwareDynamicThreatDefenseActionForNetworkType;
  targetedAttack: AntimalwareDynamicThreatDefenseProtectionLevel;
  suspiciousFilesAndNetworkTraffic: AntimalwareDynamicThreatDefenseProtectionLevel;
  exploits: AntimalwareDynamicThreatDefenseProtectionLevel;
  ransomware: AntimalwareDynamicThreatDefenseProtectionLevel;
  grayware: AntimalwareDynamicThreatDefenseProtectionLevel;
}
export interface AntimalwareDynamicThreatDefenseProtectionLevel {
  enable: boolean;
  detectionLevel: AntimalwareDynamicThreatDefenseDetectionLevelLevelType;
}

// antiExploit
export interface AntimalwareAntiExploitSection {
  enable: boolean;
  predefinedApplications: AntimalwareAntiExploitApplication[];
  customApplications: AntimalwareAntiExploitApplication[];
  systemWideDetections: AntimalwareAntiExploitSystemWideDetectionsWindows;
  linux: {
    systemWideDetections: AntimalwareAntiExploitSystemWideDetectionsLinux;
  };
}
export interface AntimalwareAntiExploitApplication {
  details: {
    applicationName: string;
    processName: string[];
    status: AntimalwareAntiExploitApplicationStatus;
  };
  exploitDetectionTechniques: AntimalwareAntiExploitDetectionTechniques[];
}
export interface AntimalwareAntiExploitDetectionTechniques {
  name: AntimalwareAntiExploitDetectionTechniquesName;
  action: AntimalwareAntiExploitSystemWideDetectionsLinuxAction;
  enable: boolean;
}
export interface AntimalwareAntiExploitSystemWideDetectionsWindows {
  privilegeEscalationStatus: boolean;
  privilegeEscalation: AntimalwareAntiExploitSystemWideDetectionsWindowsAction;
  dpiDetectionsStatus: boolean;
  dpiDetections: AntimalwareAntiExploitSystemWideDetectionsWindowsAction;
  lsassMemoryAccessFromUnknownProcess: AntimalwareAntiExploitSystemWideDetectionsWindowsAction;
  lsassMemoryAccessFromUnknownProcessStatus: boolean;
}
export interface AntimalwareAntiExploitSystemWideDetectionsLinux {
  credentialsStatus: boolean;
  credentials: AntimalwareAntiExploitSystemWideDetectionsLinuxAction;
  ptraceStatus: boolean;
  ptrace: AntimalwareAntiExploitSystemWideDetectionsLinuxAction;
  namespaceStatus: boolean;
  namespace: AntimalwareAntiExploitSystemWideDetectionsLinuxAction;
  corruptionStatus: boolean;
  corruption: AntimalwareAntiExploitSystemWideDetectionsLinuxAction;
  permissionsStatus: boolean;
  permissions: AntimalwareAntiExploitSystemWideDetectionsLinuxAction;
}

// securityServers
export interface AntimalwareSecurityServersSection {
  svaIps: AntimalwareSecurityServersSvaIp[];
  svaProxy: {
    profile: AntimalwareSecurityServersSvaProxyProfile;
  };
  affinityRule: boolean;
  enableOnDemandSlots: boolean;
  onDemandSlotsValues?: AntimalwareSecurityServersOnDemandSlotsValues;
  onDemandSlots?: number;
  useSSL: boolean;
  svaMpAffinityRules?: boolean;
  serverOrder: AntimalwareSecurityServersOrder;
}

export interface AntimalwareSecurityServersSvaIp {
  endpointId: string;
  companyId: string;
  name: string;
  ip: string;
  customHostname: string;
}
export interface UnifiedEndpointsSvaMachineToUpdate {
  _id: string;
  customHostname: string;
  protectedEntityId: string;
  ip: string;
}
export interface UnifiedEndpointsSvaMachine {
  _id: string;
  protectedEntityId: string;
  name: string;
  type: number;
  ip: string;
  customHostname: string;
}

// onExecute
export interface AntimalwareOnExecuteSection {
  virusControl: {
    enable: boolean;
    profile: AntimalwareOnExecuteVirusControlProfile;
    defaultAction: AntimalwareOnExecuteVirusControlDefaultAction;
    kernelApiMonitor: {
      enabled: boolean;
    };
  };
  commandLineScanning: {
    filelessAttackProtection: boolean;
    enable: boolean;
    amsi: {
      enable: boolean;
      reportDetectionsToCaller: boolean;
    };
  };
  ransomwareRemediation: {
    enable: boolean;
    localAttack: {
      enable: boolean;
    };
    remoteAttack: {
      enable: boolean;
    };
    restore: {
      mode: AntimalwareOnExecuteRansomwareRemediationRestoreMode;
    };
    efsProtection: {
      enabled: boolean;
    };
  };
  theta: {
    enabled: boolean;
  };
  sensitiveRegistryProtection?: {
    enable: boolean;
    action: AntimalwareOnExecuteSensitiveRegistryProtectionAction;
  };
}

// onDemand
export interface AntimalwareOnDemandSection {
  scanTask: AntimalwareOnDemandScanTask[];
  deviceScanning: AntimalwareOnDemandDeviceScan;
  contextualScan: AntimalwareOnDemandContextualScan;
}

export interface AntimalwareOnDemandDeviceScanSettings {
  scanProfile: AntimalwareOnDemandScanningProfileType;
  scanSettings: {
    fileTypes: AntimalwareFileTypesStatus;
    extensions: string[];
    action: {
      whenInfected: {
        action: AntimalwareOnDemandInfectedAction;
      };
      whenRootKitAction: AntimalwareOnDemandRootkitAction;
      whenPua: {
        action: AntimalwareOnDemandPuaAction;
      };
    };
    archives: {
      enable: boolean;
      scanEmail: boolean;
      settings: {
        limitArchiveSize: number;
        maximumArchiveDepth: AntimalwareArchivesDepth;
      };
    };
    miscellaneous: {
      ignoreKeyloggers: boolean;
      scanBoot: boolean;
      scanCookie: boolean;
      scanMemory: boolean;
      scanNetworkFiles: boolean;
      scanNewChanged: boolean;
      scanPUA: boolean;
      scanRegistry: boolean;
      scanRootkits: boolean;
    };
  };
}
export interface AntimalwareOnDemandDeviceScan extends AntimalwareOnDemandDeviceScanSettings {
  enable: boolean;
  settings: {
    automaticallyScanCdDvd: boolean;
    automaticallyScanUsb: boolean;
    scanDevices: {
      enable: boolean;
      maxim: number;
    };
  };
}

export interface AntimalwareOnDemandContextualScan {
  scanProfile: AntimalwareOnDemandScanningProfileType;
  scanSettings: {
    archives: {
      enable: boolean;
      scanEmail: boolean;
      settings: {
        limitArchiveSize: number;
        maximumArchiveDepth: AntimalwareArchivesDepth;
      };
    };
    miscellaneous: {
      ignoreKeyloggers: boolean;
      scanBoot: boolean;
      scanCookie: boolean;
      scanMemory: boolean;
      scanNetworkFiles: boolean;
      scanNewChanged: boolean;
      scanPUA: boolean;
      scanRegistry: boolean;
      scanRootkits: boolean;
    };
    action: {
      whenInfected: {
        action: AntimalwareOnDemandInfectedAction;
      };
      whenRootKitAction: AntimalwareOnDemandRootkitAction;
      whenPua: {
        action: AntimalwareOnDemandPuaAction;
      };
    };
  };
}

export interface AntimalwareOnDemandScanTask {
  scanTaskHash: string;
  uniqueId: string;
  taskName: string;
  scanType: AntimalwareOnDemandScanTaskScanType;
  recovery: {
    interval: number;
    runPer: RepeatInterval;
    mode: {
      runImmediately: boolean;
      runScheduledBefore: boolean;
    };
  };
  scheduler: {
    recurrenceType: RecurrenceType;
    recurrentDaySun: number;
    recurrentDayMon: number;
    recurrentDayTue: number;
    recurrentDayWed: number;
    recurrentDayThu: number;
    recurrentDayFri: number;
    recurrentDaySat: number;
    runEvery: number;
    runPer: RepeatInterval;
    startDate: {
      date: string;
      hours: number;
      minutes: number;
    };
  };
  scanProfile: AntimalwareOnDemandScanningProfileType;
  scanSettings: {
    fileTypes: AntimalwareFileTypesStatus;
    extensions: string[];
    archives: {
      enable: boolean;
      settings: {
        limitArchiveSize: number;
        maximumArchiveDepth: AntimalwareArchivesDepth;
      };
      scanEmail: boolean;
    };
    miscellaneous: {
      scanBoot: boolean;
      scanUefi: boolean;
      scanRegistry: boolean;
      scanRootkits: boolean;
      ignoreKeyloggers: boolean;
      scanMemory: boolean;
      scanCookie: boolean;
      scanNewChanged: boolean;
      runTask: boolean;
      shutDownWhenFinished: boolean;
      scanPUA: boolean;
      scanNetworkFiles: boolean;
      resumeIfInterrupted: boolean;
      preserveLastAccessTime: boolean;
    };
    action: {
      whenInfected: {
        action: AntimalwareOnDemandInfectedAction;
      };
      whenRootKitAction: AntimalwareOnDemandRootkitAction;
      whenPua: {
        action: AntimalwareOnDemandPuaAction;
      };
    };
    networkCredentials?: {
      user: string;
      password: string;
    };
    scannerId?: string;
  };
  scanTarget: AntimalwareOnDemandScanTarget[];
  exclusions: {
    profile: AntimalwareOnDemandExclusionProfile;
    taskList: AntimalwareOnDemandExclusionTask[];
  };
  pauseWhenOnBattery: boolean;
  cpuUsageControl: {
    enabled: boolean;
    maximumUsage: AntimalwareOnDemandScanTaskCpuMaxUsage;
  };
}

export interface AntimalwareOnDemandExclusionTask {
  taskType: AntimalwareOnDemandExclusionTaskType;
  path: string;
}

export interface AntimalwareOnDemandScanTarget {
  path: string;
}

// enums
export enum AntimalwareDynamicThreatDefenseDetectionLevelLevelType {
  PERMISSIVE = 1,
  NORMAL = 2,
  AGGRESSIVE = 3,
}
export enum AntimalwareDynamicThreatDefenseActionForLocalType {
  DENY = 1,
  REMEDIATE = 2,
  MOVE_TO_QUARANTINE = 4,
  REPORT_ONLY = 5,
}
export enum AntimalwareDynamicThreatDefenseActionForNetworkType {
  BLOCK = 1,
  REPORT_ONLY = 2,
}

export enum AntimalwareAntiTamperingVulnerableDriversAction {
  DENY_ACCESS = 1,
  DISINFECT = 2,
  REPORT_ONLY = 3,
}

export enum AntimalwareAntiTamperingRebootTimes {
  IMMEDIATELY = 0,
  MIN_5 = 5,
  MIN_10 = 10,
  MIN_15 = 15,
}

export enum AntimalwareOnAccessScanningProfileType {
  AGGRESSIVE = 1,
  NORMAL = 2,
  PERMISSIVE = 3,
  CUSTOM = 4,
}

export enum AntimalwareArchivesDepth {
  TWO = 2,
  FOUR = 4,
  SIX = 6,
  EIGHT = 8,
  TEN = 10,
  TWELVE = 12,
  FOURTEEN = 14,
  SIXTEEN = 16,
}

export enum AntimalwareOnAccessScanningGeneralScanActionInfectedFilesAction {
  DENY_ACCESS = 0,
  REMEDIATE = 1,
  MOVE_TO_QUARANTINE = 4,
  REPORT_ONLY = 6,
}

export enum AntimalwareOnAccessScanningGeneralScanActionNetworkFilesAction {
  DENY_ACCESS = 0,
  REMEDIATE = 1,
  MOVE_TO_QUARANTINE = 4,
  REPORT_ONLY = 6,
}

export enum AntimalwareFileTypesStatus {
  ALL_FILES = 1,
  APPLICATIONS_ONLY = 2,
  USER_DEFINED_EXTENTIONS = 3,
}

export enum AntimalwareAntiExploitDetectionTechniquesName {
  ROP_EMULATION = 1,
  ROP_STACK_PIVOT = 2,
  ROP_ILLEGAL_CALL = 3,
  ROP_STACK_MISALIGNED = 4,
  ROP_RETURN_TO_STACK = 5,
  ROP_MAKE_STACK_EXECUTABLE = 6,
  FLASH_GENERIC = 7,
  SHELLCODE_EXECUTION = 8,
  SHELLCODE_LOADLIBRARY_UNC = 9,
  ANTI_DETOUR = 10,
  FLASH_PAYLOAD = 11,
  ROP_CREATE_THREAD = 12,
  ANTI_METERPRETER = 13,
  OBSOLETE_PROCESS_CREATION = 14,
  CHILD_PROCESS_CREATION = 15,
  ENFORCE_WINDOWS_DEP = 16,
  ENFORCE_MODULE_RELOCATION = 17,
  VBSCRIPT_GENERIC = 18,
  SHELLCODE_EAF = 19,
  EMERGING_EXPLOITS = 20,
}
export enum AntimalwareAntiExploitSystemWideDetectionsWindowsAction {
  REPORT_ONLY = 0,
  KILL_PROCESS = 1,
  BLOCK_PROCESS = 3,
  BLOCK_AND_REPORT = 4,
}
export enum AntimalwareAntiExploitApplicationStatus {
  DISABLE = 0,
  DEFAULT = 1,
  CUSTOM = 2,
}

export enum AntimalwareAntiExploitSystemWideDetectionsLinuxAction {
  REPORT_ONLY = 0,
  KILL_PROCESS = 1,
}

export enum AntimalwareSecurityServersOnDemandSlotsValues {
  LOW = 1,
  MEDIUM = 2,
  HIGH = 3,
  CUSTOM = 4,
}

export enum AntimalwareSecurityServersSvaProxyProfile {
  KEEP_CURRENT_SETTINGS = 1,
  ENABLE_PROXY = 2,
  DISABLE_PROXY = 3,
}

export enum AntimalwareSecurityServersOrder {
  REDUNDANCY = 'priority',
  BALANCED = 'balanced',
}

export enum AntimalwareOnExecuteRansomwareRemediationRestoreMode {
  AUTOMATIC = 1,
  ON_DEMAND = 2,
}
export enum AntimalwareOnExecuteVirusControlProfile {
  AGGRESSIVE = 1,
  NORMAL = 2,
  PERMISSIVE = 3,
}
export enum AntimalwareOnExecuteVirusControlDefaultAction {
  BLOCK = 1,
  REPORT_ONLY = 2,
  REMEDIATE = 3,
}
export enum AntimalwareOnExecuteSensitiveRegistryProtectionAction {
  REPORT_ONLY = 1,
  KILL_PROCESS = 2,
}

export enum AntimalwareOnDemandExclusionProfile {
  GLOBAL_EXCLUSION = 1,
  DEFINED_EXCLUSIONS = 2,
}

export enum AntimalwareOnDemandExclusionTaskType {
  FILE = 1,
  FOLDER = 2,
  EXTENSION = 3,
  PROCESS = 4,
}

export enum AntimalwareOnDemandInfectedAction {
  REPORT_ONLY = 1,
  REMEDIATE = 2,
  MOVE_TO_QUARANTINE = 4,
}

export enum AntimalwareOnDemandRootkitAction {
  REPORT_ONLY = 1,
  REMEDIATE = 2,
}

export enum AntimalwareOnDemandPuaAction {
  REPORT_ONLY = 1,
  REMEDIATE = 2,
  MOVE_TO_QUARANTINE = 4,
}

export enum AntimalwareOnDemandScanTaskScanType {
  QUICK = 1,
  FULL = 2,
  CUSTOM = 3,
  NETWORK = 4,
  CONTEXTUAL = 5,
  DEVICE = 6,
}

export enum AntimalwareOnDemandScanTaskCpuMaxUsage {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
}

export enum AntimalwareOnDemandScanningProfileType {
  AGGRESSIVE = 1,
  NORMAL = 2,
  PERMISSIVE = 3,
  CUSTOM = 4,
}
